import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { loginService } from "../../utils/services/loginService.js";

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0% { transform: scale(0.92); opacity: 0.55; }
  50% { transform: scale(1.08); opacity: 0.15; }
  100% { transform: scale(0.92); opacity: 0.55; }
`;

const bounce = keyframes`
  0%, 100% { transform: translateX(0); opacity: 1; }
  50% { transform: translateX(6px); opacity: 0.5; }
`;

const BrandAvatar = ({ client, size = 44, primaryColor }) => {
  const label = client?.business_name || client?.name || "";
  return client?.logo ? (
    <Box
      component="img"
      src={client.logo}
      alt={label}
      sx={{
        width: size,
        height: size,
        borderRadius: "14px",
        objectFit: "contain",
        backgroundColor: "#fff",
        border: "1px solid var(--border-color)",
        flexShrink: 0,
      }}
    />
  ) : (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Poppins, sans-serif",
        fontWeight: 700,
        fontSize: size * 0.34,
        color: "#fff",
        background: `linear-gradient(135deg, ${primaryColor} 0%, #6366f1 100%)`,
        flexShrink: 0,
      }}
    >
      {getInitials(label)}
    </Box>
  );
};

const SwitchBrandModal = ({ open, onClose, clients = [], currentClientId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const primaryColor = theme.palette?.primary?.main || "#0040FF";

  const [selectedClient, setSelectedClient] = useState(null);
  const [phase, setPhase] = useState("select"); // select | confirm | switching
  const [error, setError] = useState("");

  const currentClient = useMemo(
    () => clients.find((c) => c._id === currentClientId) || null,
    [clients, currentClientId],
  );

  const resetAndClose = () => {
    if (phase === "switching") return; // cannot dismiss mid-switch
    setPhase("select");
    setSelectedClient(null);
    setError("");
    onClose && onClose();
  };

  const handlePickClient = (client) => {
    setError("");
    setSelectedClient(client);
  };

  const handleSubmit = () => {
    if (!selectedClient) {
      toast.error("Please select a brand to continue.");
      return;
    }
    if (selectedClient._id === currentClientId) {
      toast.info("You're already on this brand.");
      return;
    }
    setPhase("confirm");
  };

  const performSwitch = async () => {
    const baseToken = localStorage.getItem("base_token");
    if (!baseToken) {
      toast.error("Your session needs a refresh. Please log in again.");
      return;
    }
    setPhase("switching");
    setError("");
    try {
      const [response] = await Promise.all([
        loginService.loginWithClient(baseToken, selectedClient._id),
        new Promise((resolve) => setTimeout(resolve, 1200)),
      ]);

      localStorage.setItem("token", response.accessToken);
      localStorage.setItem("client_id", selectedClient._id);
      localStorage.setItem(
        "roles",
        JSON.stringify(response.authentication?.payload?.roles || []),
      );
      if (response.user) {
        localStorage.setItem("user", JSON.stringify(response.user));
      }
      localStorage.removeItem("sidemenuItems");

      try {
        const omsResponse = await loginService.login_oms(response);
        if (omsResponse?.token) {
          localStorage.setItem("rl_oms_token", omsResponse.token);
        }
        if (omsResponse?.user) {
          localStorage.setItem("rl_oms_user", JSON.stringify(omsResponse.user));
        }
      } catch (omsError) {
        console.error(omsError);
      }

      navigate("/", { replace: true });
      window.location.reload();
    } catch (err) {
      console.error(err);
      setError("Couldn't switch brands. Please try again.");
      setPhase("confirm");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      disableEscapeKeyDown={phase === "switching"}
      aria-labelledby="switch-brand-title"
      PaperProps={{
        sx: {
          width: { xs: "92vw", sm: 460 },
          maxWidth: "95vw",
          borderRadius: "20px",
          backgroundColor: "var(--bg-paper)",
          backgroundImage: "none",
          border: "1px solid var(--border-color)",
          overflow: "hidden",
        },
      }}
    >
      {phase === "select" && (
        <>
          <DialogTitle
            id="switch-brand-title"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 2.5,
              px: 3,
              borderBottom: "1px solid var(--border-color)",
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: `${primaryColor}1a`,
                color: primaryColor,
                flexShrink: 0,
              }}
            >
              <SwapHorizRoundedIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                fontFamily="Poppins, sans-serif"
                fontSize="17px"
                fontWeight={600}
                color="var(--text-primary)"
                lineHeight={1.3}
              >
                Switch Brand
              </Typography>
              <Typography
                fontFamily="Poppins, sans-serif"
                fontSize="12.5px"
                color="var(--text-secondary)"
              >
                Choose which brand workspace to switch into
              </Typography>
            </Box>
            <IconButton onClick={resetAndClose} size="small">
              <CloseIcon sx={{ fontSize: 20, color: "var(--text-secondary)" }} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 2, py: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                maxHeight: 340,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              {clients.map((client) => {
                const isSelected =
                  (selectedClient?._id || currentClientId) === client._id;
                const isCurrent = client._id === currentClientId;
                return (
                  <Box
                    key={client._id}
                    onClick={() => handlePickClient(client)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "14px",
                      cursor: "pointer",
                      border: isSelected
                        ? `1.5px solid ${primaryColor}`
                        : "1.5px solid var(--border-color)",
                      backgroundColor: isSelected
                        ? `${primaryColor}0d`
                        : "transparent",
                      transition: "all 0.18s ease",
                      "&:hover": {
                        backgroundColor: isSelected
                          ? `${primaryColor}14`
                          : "var(--hover-bg)",
                      },
                    }}
                  >
                    <BrandAvatar client={client} primaryColor={primaryColor} />
                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      <Typography
                        fontFamily="Poppins, sans-serif"
                        fontSize="14px"
                        fontWeight={600}
                        color="var(--text-primary)"
                        noWrap
                      >
                        {client.business_name || client.name}
                      </Typography>
                      {client.email && (
                        <Typography
                          fontFamily="Poppins, sans-serif"
                          fontSize="12px"
                          color="var(--text-secondary)"
                          noWrap
                        >
                          {client.email}
                        </Typography>
                      )}
                    </Box>
                    {isCurrent ? (
                      <Box
                        sx={{
                          px: 1.1,
                          py: 0.4,
                          borderRadius: "999px",
                          fontSize: "11px",
                          fontWeight: 600,
                          fontFamily: "Poppins, sans-serif",
                          color: "var(--text-tertiary)",
                          border: "1px solid var(--border-color)",
                          flexShrink: 0,
                        }}
                      >
                        Current
                      </Box>
                    ) : isSelected ? (
                      <CheckCircleRoundedIcon
                        sx={{ color: primaryColor, fontSize: 22, flexShrink: 0 }}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
            <Button
              onClick={resetAndClose}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderRadius: 2,
                borderColor: "var(--border-color)",
                color: "var(--text-secondary)",
                fontWeight: 600,
                textTransform: "none",
                fontSize: 14,
                fontFamily: "Poppins, sans-serif",
                "&:hover": {
                  borderColor: "var(--text-secondary)",
                  backgroundColor: "transparent",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={
                !selectedClient || selectedClient._id === currentClientId
              }
              sx={{
                minWidth: 130,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${primaryColor} 0%, #6366f1 100%)`,
                fontWeight: 600,
                textTransform: "none",
                fontSize: 14,
                fontFamily: "Poppins, sans-serif",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: `0 4px 14px 0 ${primaryColor}40`,
                },
                "&.Mui-disabled": {
                  background: "var(--border-color)",
                  color: "var(--text-tertiary)",
                },
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </>
      )}

      {phase === "confirm" && (
        <>
          <DialogContent sx={{ px: 4, pt: 4, pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <BrandAvatar
                  client={currentClient}
                  primaryColor={primaryColor}
                  size={52}
                />
                <SwapHorizRoundedIcon
                  sx={{ fontSize: 28, color: primaryColor }}
                />
                <BrandAvatar
                  client={selectedClient}
                  primaryColor={primaryColor}
                  size={52}
                />
              </Box>
              <Typography
                fontFamily="Poppins, sans-serif"
                fontSize="16px"
                fontWeight={600}
                color="var(--text-primary)"
              >
                Switch to{" "}
                {selectedClient?.business_name || selectedClient?.name}?
              </Typography>
              <Typography
                fontFamily="Poppins, sans-serif"
                fontSize="13px"
                color="var(--text-secondary)"
                sx={{ maxWidth: 340, lineHeight: 1.6 }}
              >
                You'll be switched out of{" "}
                {currentClient?.business_name || currentClient?.name || "your current brand"}{" "}
                and into{" "}
                {selectedClient?.business_name || selectedClient?.name}'s
                workspace.
              </Typography>
              {error && (
                <Typography
                  fontFamily="Poppins, sans-serif"
                  fontSize="12.5px"
                  color="#dc2626"
                >
                  {error}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ px: 3, py: 2.5, gap: 1.5, justifyContent: "center" }}
          >
            <Button
              onClick={() => setPhase("select")}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderRadius: 2,
                borderColor: "var(--border-color)",
                color: "var(--text-secondary)",
                fontWeight: 600,
                textTransform: "none",
                fontSize: 14,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Go Back
            </Button>
            <Button
              onClick={performSwitch}
              variant="contained"
              sx={{
                minWidth: 150,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${primaryColor} 0%, #6366f1 100%)`,
                fontWeight: 600,
                textTransform: "none",
                fontSize: 14,
                fontFamily: "Poppins, sans-serif",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: `0 4px 14px 0 ${primaryColor}40`,
                },
              }}
            >
              Confirm Switch
            </Button>
          </DialogActions>
        </>
      )}

      {phase === "switching" && (
        <DialogContent sx={{ px: 4, py: 6 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 3,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 150,
                height: 64,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                }}
              >
                <BrandAvatar
                  client={currentClient}
                  primaryColor={primaryColor}
                  size={56}
                />
              </Box>

              <Box
                sx={{
                  position: "relative",
                  width: 38,
                  height: 38,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: `${primaryColor}33`,
                    animation: `${pulse} 1.6s ease-in-out infinite`,
                  }}
                />
                <SwapHorizRoundedIcon
                  sx={{
                    fontSize: 26,
                    color: primaryColor,
                    zIndex: 1,
                    animation: `${bounce} 1.2s ease-in-out infinite`,
                  }}
                />
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: -4,
                    borderRadius: "18px",
                    border: `2px solid ${primaryColor}`,
                    borderTopColor: "transparent",
                    animation: `${spin} 1s linear infinite`,
                  }}
                />
                <BrandAvatar
                  client={selectedClient}
                  primaryColor={primaryColor}
                  size={56}
                />
              </Box>
            </Box>

            <Box>
              <Typography
                fontFamily="Poppins, sans-serif"
                fontSize="15px"
                fontWeight={600}
                color="var(--text-primary)"
              >
                Switching brand&hellip;
              </Typography>
              <Typography
                fontFamily="Poppins, sans-serif"
                fontSize="12.5px"
                color="var(--text-secondary)"
                sx={{ mt: 0.5 }}
              >
                {currentClient?.business_name || currentClient?.name} &rarr;{" "}
                {selectedClient?.business_name || selectedClient?.name}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default SwitchBrandModal;
